import { VideoFormat } from '@/lib/utils'
import { LOCAL_LIBRARY_ID, rememberLocalMode, writeLocalJson } from '@/lib/browser-persistence'

// Re-export the interface for compatibility
export interface StoredVideo {
  id: string
  name: string
  url: string
  format: VideoFormat | null
  size: number
  uploadDate: string
  userId: string | null
  duration?: string
  frameCount?: number
  thumbnail?: string
  mimeType?: string
  fileData?: string
}

interface StoredVideoRecord extends StoredVideo {
  blob?: Blob
}

const VIDEO_LIBRARY_DB = 'framex-local-video-library'
const VIDEO_LIBRARY_VERSION = 1
const VIDEOS_STORE = 'videos'
const VIDEO_METADATA_KEY = 'framex.videoLibrary.metadata'

let videoDb: IDBDatabase | null = null

function openVideoLibrary(): Promise<IDBDatabase> {
  if (videoDb) return Promise.resolve(videoDb)

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(VIDEO_LIBRARY_DB, VIDEO_LIBRARY_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => {
      videoDb = request.result
      resolve(request.result)
    }
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(VIDEOS_STORE)) {
        db.createObjectStore(VIDEOS_STORE, { keyPath: 'id' })
      }
    }
  })
}

async function getVideoStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  const db = await openVideoLibrary()
  return db.transaction([VIDEOS_STORE], mode).objectStore(VIDEOS_STORE)
}

function toStoredVideo(record: StoredVideoRecord): StoredVideo {
  return {
    ...record,
    url: record.fileData || (record.blob ? URL.createObjectURL(record.blob) : record.url),
    thumbnail: record.thumbnail,
    fileData: undefined,
  }
}

function persistVideoMetadata(videos: StoredVideo[]): void {
  writeLocalJson(VIDEO_METADATA_KEY, videos.map(({ fileData: _fileData, ...video }) => video))
  rememberLocalMode()
}

async function loadVideoRecords(): Promise<StoredVideoRecord[]> {
  const store = await getVideoStore('readonly')
  return new Promise((resolve, reject) => {
    const request = store.getAll()
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result || [])
  })
}

// Get all stored videos from this browser.
export async function getStoredVideos(): Promise<StoredVideo[]> {
  try {
    const videos = (await loadVideoRecords()).map(toStoredVideo)
    persistVideoMetadata(videos)
    return videos.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())
  } catch (error) {
    console.error('Failed to get stored videos:', error)
    return []
  }
}

export async function getUserVideos(_userId: string | null): Promise<StoredVideo[]> {
  return getStoredVideos() 
}

export async function storeVideo(video: Omit<StoredVideo, 'id' | 'uploadDate'>): Promise<StoredVideo> {
  const storedVideo: StoredVideoRecord = {
    ...video,
    id: crypto.randomUUID(),
    uploadDate: new Date().toISOString(),
    userId: LOCAL_LIBRARY_ID,
  }

  const store = await getVideoStore('readwrite')
  await new Promise<void>((resolve, reject) => {
    const request = store.put(storedVideo)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })

  const videos = await getStoredVideos()
  persistVideoMetadata(videos)
  return toStoredVideo(storedVideo)
}

export async function storeVideoWithFile(
  file: File,
  additionalData: Omit<StoredVideo, 'id' | 'uploadDate' | 'name' | 'url' | 'size' | 'fileData' | 'mimeType'>
): Promise<StoredVideo> {
  try {
    const storedVideo: StoredVideoRecord = {
      id: crypto.randomUUID(),
      name: file.name,
      url: '',
      format: additionalData.format ?? null,
      size: file.size,
      uploadDate: new Date().toISOString(),
      userId: LOCAL_LIBRARY_ID,
      duration: additionalData.duration,
      frameCount: additionalData.frameCount,
      mimeType: file.type,
      blob: file,
    }

    const store = await getVideoStore('readwrite')
    await new Promise<void>((resolve, reject) => {
      const request = store.put(storedVideo)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })

    const videos = await getStoredVideos()
    persistVideoMetadata(videos)
    return toStoredVideo(storedVideo)
  } catch (error) {
    console.error('Failed to store video:', error)
    throw error 
  }
}

export async function getVideoById(videoId: string): Promise<StoredVideo | null> {
  try {
    const store = await getVideoStore('readonly')
    return await new Promise((resolve, reject) => {
      const request = store.get(videoId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result ? toStoredVideo(request.result) : null)
    })
  } catch (e) {
    console.error("Error fetching video", e);
    return null;
  }
}

export async function getVideoByIdWithUrl(videoId: string): Promise<StoredVideo | null> {
  return getVideoById(videoId);
}

export async function deleteVideo(videoId: string): Promise<void> {
  const store = await getVideoStore('readwrite')
  await new Promise<void>((resolve, reject) => {
    const request = store.delete(videoId)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })
  const videos = await getStoredVideos()
  persistVideoMetadata(videos)
}

export async function updateVideoMetadata(videoId: string, updates: Partial<StoredVideo>): Promise<StoredVideo | null> {
  const store = await getVideoStore('readwrite')
  const existing = await getRawVideoRecord(videoId)
  if (!existing) return null

  const updated: StoredVideoRecord = { ...existing, ...updates, userId: LOCAL_LIBRARY_ID }
  await new Promise<void>((resolve, reject) => {
    const request = store.put(updated)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })
  const videos = await getStoredVideos()
  persistVideoMetadata(videos)
  return toStoredVideo(updated)
}

// Update video thumbnail
export async function updateVideoThumbnail(id: string, thumbnail: string): Promise<void> {
  try {
    await updateVideoMetadata(id, { thumbnail })
  } catch (error) {
     console.error('Failed to update video thumbnail:', error);
     throw error;
  }
}

export async function duplicateStoredVideo(videoId: string): Promise<StoredVideo> {
  const existing = await getRawVideoRecord(videoId)
  if (!existing) {
    throw new Error('Video not found')
  }

  const duplicate: StoredVideoRecord = {
    ...existing,
    id: crypto.randomUUID(),
    name: `Copy of ${existing.name}`,
    uploadDate: new Date().toISOString(),
    userId: LOCAL_LIBRARY_ID,
  }

  const store = await getVideoStore('readwrite')
  await new Promise<void>((resolve, reject) => {
    const request = store.put(duplicate)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })

  const videos = await getStoredVideos()
  persistVideoMetadata(videos)
  return toStoredVideo(duplicate)
}

async function getRawVideoRecord(videoId: string): Promise<StoredVideoRecord | null> {
  const store = await getVideoStore('readonly')
  return new Promise((resolve, reject) => {
    const request = store.get(videoId)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result || null)
  })
}

